import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * Site footer (docs/prd-homepage-redesign.md §8.2, prototype `.ftr`): one warm band with
 * no divider lines, three link columns, copyright and the domain on the bottom row.
 * On phones Explore and About sit side by side and the policy links collapse to one line.
 */
export default function Footer() {
  const { t } = useTranslation('common');

  const columns = [
    {
      title: t('footer.explore'),
      links: [
        { to: '/#stage=s2', label: t('footer.stages') },
        { to: '/activities', label: t('footer.allActivities') },
        { to: '/picks', label: t('footer.picks') },
        { to: '/gift-cards', label: t('footer.giftCards') },
      ],
    },
    {
      title: t('footer.about'),
      links: [
        { to: '/#about', label: t('footer.aboutUs') },
        { to: '/apply-host', label: t('footer.becomeHost') },
        { to: '/training-program', label: t('footer.training') },
        { to: '/investors', label: t('footer.investors') },
        { to: '/contact-us', label: t('footer.contact') },
      ],
    },
    {
      title: t('footer.policies'),
      links: [
        { to: '/refund-policy', label: t('footer.refund') },
        { to: '/terms-of-use', label: t('footer.terms') },
      ],
    },
  ];

  return (
    <footer className="bg-foot pb-5 pt-[30px] font-text text-ink-2 desk:pb-[26px] desk:pt-[42px]">
      <div className="mx-auto max-w-[1160px] px-4 desk:px-7">
        <div className="flex flex-wrap justify-between gap-[22px] desk:gap-[30px]">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2.5 font-ui text-[18px] font-bold text-primary desk:text-[21px]"
            >
              <img src="/logo.svg" alt="" className="h-[30px] w-[30px] desk:h-9 desk:w-9" />
              <span>YOYO Club</span>
            </Link>
            <p className="mt-2 max-w-[22em] text-sm">{t('footer.tagline')}</p>
          </div>
          <div className="grid w-full grid-cols-2 gap-x-4 gap-y-[18px] desk:flex desk:w-auto desk:flex-wrap desk:gap-[50px]">
            {columns.map((col, i) => {
              const isPolicies = i === columns.length - 1;
              return (
                <div key={col.title} className={isPolicies ? 'col-span-2 desk:col-span-1' : ''}>
                  <h4
                    className={`mb-2 font-ui text-xs leading-[1.6] font-semibold tracking-[0.08em] text-mute ${
                      isPolicies ? 'hidden desk:block' : ''
                    }`}
                  >
                    {col.title}
                  </h4>
                  {col.links.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={
                        isPolicies
                          ? 'mr-[18px] inline-block py-1 text-[13px] text-mute hover:text-primary desk:mr-0 desk:block desk:text-[14.5px] desk:text-ink-2'
                          : 'block py-1 text-[14.5px] text-ink-2 hover:text-primary'
                      }
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-between gap-2.5 text-[13px] text-mute desk:mt-[34px]">
          <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
          <span className="font-ui font-semibold text-primary">sayyoyo.com</span>
        </div>
      </div>
    </footer>
  );
}
